import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, LocationOn, School as SchoolIcon, Search } from '@mui/icons-material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

type SchoolStatus = 'active' | 'inactive' | 'suspended';

interface KitchenProfile {
  id: string;
  kitchenCode: string;
  kitchenName: string;
  locationCode?: string;
  locationName?: string;
  kitchenType: 'central' | 'satellite';
  latitude?: number;
  longitude?: number;
}

interface SchoolMaster {
  id: string;
  schoolCode: string;
  schoolName: string;
  kitchenProfileId?: string;
  kitchenCode?: string;
  kitchenName?: string;
  latitude: number;
  longitude: number;
  address?: string;
  county?: string;
  subCounty?: string;
  ward?: string;
  pupilCount?: number;
  contactPerson?: string;
  contactPhone?: string;
  status: SchoolStatus;
}

interface NearbyKitchen {
  kitchen: KitchenProfile;
  distanceKm: number;
}

const defaultSchool: Partial<SchoolMaster> = {
  schoolCode: '',
  schoolName: '',
  kitchenProfileId: '',
  latitude: undefined,
  longitude: undefined,
  address: '',
  county: '',
  subCounty: '',
  ward: '',
  pupilCount: 0,
  contactPerson: '',
  contactPhone: '',
  status: 'active',
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const apiError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const message = err.response?.data?.message;
    return Array.isArray(message) ? message.join(', ') : message || err.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const toNumberOrUndefined = (value: unknown): number | undefined => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const kitchenLabel = (kitchen: KitchenProfile) => {
  const name = kitchen.locationName || kitchen.kitchenName;
  const code = kitchen.locationCode || kitchen.kitchenCode;
  return `${name} (${code})`;
};

const distanceKm = (from: { latitude: number; longitude: number }, to: { latitude: number; longitude: number }) => {
  const earthRadiusKm = 6371;
  const toRad = (value: number) => value * Math.PI / 180;
  const dLat = toRad(to.latitude - from.latitude);
  const dLon = toRad(to.longitude - from.longitude);
  const lat1 = toRad(from.latitude);
  const lat2 = toRad(to.latitude);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return Number((earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))).toFixed(3));
};

const SchoolsTab: React.FC = () => {
  const [schools, setSchools] = useState<SchoolMaster[]>([]);
  const [kitchens, setKitchens] = useState<KitchenProfile[]>([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [kitchenFilter, setKitchenFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState<SchoolMaster | null>(null);
  const [form, setForm] = useState<Partial<SchoolMaster>>(defaultSchool);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [deleteTarget, setDeleteTarget] = useState<SchoolMaster | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [schoolsResp, kitchensResp] = await Promise.all([
        axios.get(`${API_BASE}/kitchen-ops/schools`, { headers: authHeaders() }),
        axios.get(`${API_BASE}/kitchen-ops/kitchens`, { headers: authHeaders() }),
      ]);
      const schoolList = Array.isArray(schoolsResp.data) ? schoolsResp.data : schoolsResp.data?.data ?? [];
      const kitchenList = Array.isArray(kitchensResp.data) ? kitchensResp.data : kitchensResp.data?.data ?? [];
      setSchools(schoolList);
      setKitchens(kitchenList);
    } catch (err) {
      setError(apiError(err, 'Failed to load school master data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nearbyKitchens = useMemo<NearbyKitchen[]>(() => {
    const latitude = toNumberOrUndefined(form.latitude);
    const longitude = toNumberOrUndefined(form.longitude);
    if (latitude == null || longitude == null) return [];
    return kitchens
      .filter((kitchen) => kitchen.latitude != null && kitchen.longitude != null)
      .map((kitchen) => ({
        kitchen,
        distanceKm: distanceKm(
          { latitude, longitude },
          { latitude: Number(kitchen.latitude), longitude: Number(kitchen.longitude) },
        ),
      }))
      .sort((a, b) => a.distanceKm - b.distanceKm)
      .slice(0, 5);
  }, [form.latitude, form.longitude, kitchens]);

  const filteredSchools = schools.filter((school) => {
    const query = searchText.toLowerCase();
    const matchesSearch =
      !query ||
      school.schoolCode?.toLowerCase().includes(query) ||
      school.schoolName?.toLowerCase().includes(query) ||
      school.kitchenName?.toLowerCase().includes(query) ||
      school.county?.toLowerCase().includes(query);
    const matchesStatus = !statusFilter || school.status === statusFilter;
    const matchesKitchen = !kitchenFilter || school.kitchenProfileId === kitchenFilter;
    return matchesSearch && matchesStatus && matchesKitchen;
  });

  const openAddDialog = () => {
    setEditingSchool(null);
    setForm(defaultSchool);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (school: SchoolMaster) => {
    setEditingSchool(school);
    setForm({ ...school });
    setFormErrors({});
    setDialogOpen(true);
  };

  const updateForm = (field: keyof SchoolMaster, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};
    if (!form.schoolCode?.trim()) errors.schoolCode = 'Code is required';
    if (!form.schoolName?.trim()) errors.schoolName = 'Name is required';
    const latitude = toNumberOrUndefined(form.latitude);
    const longitude = toNumberOrUndefined(form.longitude);
    if (latitude == null) errors.latitude = 'Latitude is required';
    if (longitude == null) errors.longitude = 'Longitude is required';
    if (latitude != null && (latitude < -90 || latitude > 90)) errors.latitude = 'Latitude must be between -90 and 90';
    if (longitude != null && (longitude < -180 || longitude > 180)) errors.longitude = 'Longitude must be between -180 and 180';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        schoolCode: form.schoolCode,
        schoolName: form.schoolName,
        kitchenProfileId: form.kitchenProfileId || null,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        address: form.address || undefined,
        county: form.county || undefined,
        subCounty: form.subCounty || undefined,
        ward: form.ward || undefined,
        pupilCount: Number(form.pupilCount || 0),
        contactPerson: form.contactPerson || undefined,
        contactPhone: form.contactPhone || undefined,
        status: form.status || 'active',
        [editingSchool ? 'updatedBy' : 'createdBy']: 'frontend',
      };

      if (editingSchool) {
        await axios.patch(`${API_BASE}/kitchen-ops/schools/${editingSchool.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API_BASE}/kitchen-ops/schools`, payload, { headers: authHeaders() });
      }

      setSnackbar({ open: true, message: `School ${editingSchool ? 'updated' : 'created'}`, severity: 'success' });
      setDialogOpen(false);
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: apiError(err, 'Failed to save school'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const deleteSchool = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await axios.delete(`${API_BASE}/kitchen-ops/schools/${deleteTarget.id}`, { headers: authHeaders() });
      setSnackbar({ open: true, message: 'School deleted', severity: 'success' });
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setSnackbar({ open: true, message: apiError(err, 'Failed to delete school'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'schoolCode',
      headerName: 'Code',
      width: 130,
      renderCell: (params) => <Typography variant="body2" fontWeight={600}>{params.value}</Typography>,
    },
    { field: 'schoolName', headerName: 'School', flex: 1, minWidth: 220 },
    {
      field: 'kitchenName',
      headerName: 'Serving Kitchen',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => params.value ? `${params.value}${params.row.kitchenCode ? ` (${params.row.kitchenCode})` : ''}` : '-',
    },
    { field: 'pupilCount', headerName: 'Pupils', width: 100, type: 'number' },
    { field: 'county', headerName: 'County', width: 140 },
    {
      field: 'gps',
      headerName: 'GPS',
      width: 170,
      renderCell: (params) => params.row.latitude != null && params.row.longitude != null ? `${params.row.latitude}, ${params.row.longitude}` : '-',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={String(params.value).toUpperCase()}
          size="small"
          color={params.value === 'active' ? 'success' : params.value === 'suspended' ? 'warning' : 'default'}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 90,
      getActions: (params) => [
        <GridActionsCellItem icon={<Edit />} label="Edit" onClick={() => openEditDialog(params.row)} key="edit" />,
        <GridActionsCellItem icon={<Delete />} label="Delete" onClick={() => setDeleteTarget(params.row)} key="delete" color="error" />,
      ],
    },
  ];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search schools..."
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            sx={{ minWidth: 240 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(event) => setStatusFilter(event.target.value)}>
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Serving Kitchen</InputLabel>
            <Select value={kitchenFilter} label="Serving Kitchen" onChange={(event) => setKitchenFilter(event.target.value)}>
              <MenuItem value="">All Kitchens</MenuItem>
              {kitchens.map((kitchen) => (
                <MenuItem key={kitchen.id} value={kitchen.id}>
                  {kitchenLabel(kitchen)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box flex={1} />
          <Button variant="outlined" startIcon={<LocationOn />} onClick={load} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
            Add School
          </Button>
        </Toolbar>
      </Paper>

      <Paper sx={{ height: 540 }}>
        <DataGrid
          rows={filteredSchools}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          density="compact"
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SchoolIcon />
            {editingSchool ? `Edit School: ${editingSchool.schoolCode}` : 'Add School Master'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField label="School Code *" value={form.schoolCode || ''} onChange={(event) => updateForm('schoolCode', event.target.value)} error={!!formErrors.schoolCode} helperText={formErrors.schoolCode} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="School Name *" value={form.schoolName || ''} onChange={(event) => updateForm('schoolName', event.target.value)} error={!!formErrors.schoolName} helperText={formErrors.schoolName} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <FormControl fullWidth size="small">
                <InputLabel>Serving Kitchen</InputLabel>
                <Select label="Serving Kitchen" value={form.kitchenProfileId || ''} onChange={(event) => updateForm('kitchenProfileId', event.target.value)}>
                  <MenuItem value="">Unassigned</MenuItem>
                  {kitchens.map((kitchen) => (
                    <MenuItem key={kitchen.id} value={kitchen.id}>
                      {kitchenLabel(kitchen)} - {kitchen.kitchenType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={form.status || 'active'} onChange={(event) => updateForm('status', event.target.value)}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField label="Latitude *" type="number" value={form.latitude ?? ''} onChange={(event) => updateForm('latitude', event.target.value)} error={!!formErrors.latitude} helperText={formErrors.latitude} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Longitude *" type="number" value={form.longitude ?? ''} onChange={(event) => updateForm('longitude', event.target.value)} error={!!formErrors.longitude} helperText={formErrors.longitude} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Pupil Count" type="number" value={form.pupilCount ?? ''} onChange={(event) => updateForm('pupilCount', event.target.value)} fullWidth size="small" inputProps={{ min: 0 }} />
            </Grid>

            {nearbyKitchens.length > 0 && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" gutterBottom>Nearest Kitchens</Typography>
                  <Stack spacing={1}>
                    {nearbyKitchens.map(({ kitchen, distanceKm }) => (
                      <Box key={kitchen.id} display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" flex={1}>
                          {kitchenLabel(kitchen)} - {distanceKm} km
                        </Typography>
                        <Button size="small" variant={form.kitchenProfileId === kitchen.id ? 'contained' : 'outlined'} onClick={() => updateForm('kitchenProfileId', kitchen.id)}>
                          Assign
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField label="Address" value={form.address || ''} onChange={(event) => updateForm('address', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="County" value={form.county || ''} onChange={(event) => updateForm('county', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Sub-county" value={form.subCounty || ''} onChange={(event) => updateForm('subCounty', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Ward" value={form.ward || ''} onChange={(event) => updateForm('ward', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Contact Person" value={form.contactPerson || ''} onChange={(event) => updateForm('contactPerson', event.target.value)} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Contact Phone" value={form.contactPhone || ''} onChange={(event) => updateForm('contactPhone', event.target.value)} fullWidth size="small" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} variant="contained" disabled={saving}>
            {editingSchool ? 'Update' : 'Create'} School
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete School</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.schoolName}</strong> ({deleteTarget?.schoolCode})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>Cancel</Button>
          <Button onClick={deleteSchool} variant="contained" color="error" disabled={saving}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolsTab;
